/**
 * The closed style set and fonts. One definition, compiled for the editor
 * (CSS) and the PDF (pdfmake). PLAN.md 3.8 (revised 2026-09-22).
 *
 * Two fonts, chosen per character:
 *   Telugu letters, signs and Telugu digits -> Sree Krushnadevaraya
 *   everything else (English, digits 0-9, punctuation, spaces) -> the Latin font
 *
 * The rule is purely per character, with no look at neighbouring text, so a
 * CSS unicode-range in the browser and `splitByScript` in the PDF make exactly
 * the same choice for every character. Screen and PDF cannot disagree.
 */

/* ---------------------------------------------------------------- fonts */

/** CSS family name the document text uses; both faces are registered under it. */
export const DOC_FONT_FAMILY = 'Deed';

export type FontScript = 'telugu' | 'latin';

export interface FontFile {
  /** Name shown in the licence notice. */
  displayName: string;
  /** Files under /fonts/ in the web app. Regular is required; bold may reuse it. */
  regular: string;
  bold: string | null;
  /** Italic files (owner 10-21; Telugu: a slanted copy of the regular face); null = none, the text prints upright. */
  italic: string | null;
  boldItalic: string | null;
  /** True when the file is a variable font covering 100-900. */
  variable: boolean;
  licence: string;
}

/**
 * Latin: Rockwell was requested. It is a licensed Monotype font; until a web
 * and app licence is bought, Rokkitt (SIL OFL), the closest free slab serif,
 * stands in. Swapping means replacing these entries and the files.
 */
export const FONTS: Readonly<Record<FontScript, FontFile>> = {
  telugu: {
    displayName: 'Sree Krushnadevaraya',
    regular: 'SreeKrushnadevaraya-Regular.ttf',
    bold: null, // the font has one weight; bold Telugu prints at regular weight
    // Owner 10-21 (2): italic must show on Telugu too. The font has no italic, so
    // scripts/make-telugu-slanted.py made a slanted copy (same shaping tables, renamed under the OFL).
    italic: 'DeedTeluguSlanted.ttf',
    boldItalic: 'DeedTeluguSlanted.ttf',
    variable: false,
    licence: 'SIL Open Font License 1.1',
  },
  latin: {
    displayName: 'Rokkitt (stand-in for Rockwell)',
    // Static files, not the variable font: pdfmake embeds a variable font's
    // default instance, which for Rokkitt is SemiBold, so all text came out heavy.
    regular: 'Rokkitt-Regular.ttf',
    bold: 'Rokkitt-Bold.ttf',
    // Static italics from Fonthausen/RokkittFont @762d143 (public/fonts/README.txt has the hashes).
    italic: 'Rokkitt-Italic.ttf',
    boldItalic: 'Rokkitt-BoldItalic.ttf',
    variable: false,
    licence: 'SIL Open Font License 1.1',
  },
};

/** Codepoint ranges rendered with the Telugu font. Everything else is Latin. */
export const TELUGU_RANGES: readonly (readonly [number, number])[] = [
  [0x0c00, 0x0c7f], // Telugu block: letters, vowel signs, virama, Telugu digits
  [0x0964, 0x0965], // danda, double danda
  [0x200c, 0x200d], // ZWNJ / ZWJ: joiners inside Telugu words must shape with them
];

export function scriptOf(codePoint: number): FontScript {
  for (const [lo, hi] of TELUGU_RANGES) if (codePoint >= lo && codePoint <= hi) return 'telugu';
  return 'latin';
}

export interface ScriptRun { script: FontScript; text: string }

/**
 * Split text into maximal single-script runs. Needed for the PDF: fontkit
 * shapes one run with one font and one shaper, and mixed runs break Telugu
 * shaping (Spike 1, F5).
 */
export function splitByScript(text: string): ScriptRun[] {
  const out: ScriptRun[] = [];
  for (const ch of text) {
    const script = scriptOf(ch.codePointAt(0)!);
    const last = out[out.length - 1];
    if (last?.script === script) last.text += ch;
    else out.push({ script, text: ch });
  }
  return out;
}

function unicodeRange(ranges: readonly (readonly [number, number])[]): string {
  const hex = (n: number) => n.toString(16).toUpperCase().padStart(4, '0');
  return ranges.map(([lo, hi]) => (lo === hi ? `U+${hex(lo)}` : `U+${hex(lo)}-${hex(hi)}`)).join(', ');
}

/** Complement of TELUGU_RANGES over the whole Unicode range. */
function latinRanges(): [number, number][] {
  const sorted = [...TELUGU_RANGES].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  let next = 0;
  for (const [lo, hi] of sorted) {
    if (lo > next) out.push([next, lo - 1]);
    next = hi + 1;
  }
  out.push([next, 0x10ffff]);
  return out;
}

/**
 * @font-face rules: one family, faces split by unicode-range and weight. The
 * screen uses exactly the files the PDF embeds.
 */
export function toFontFaceCss(baseUrl = '/fonts/'): string {
  const face = (src: string, weight: string, ranges: readonly (readonly [number, number])[], style: 'normal' | 'italic' = 'normal') =>
    `@font-face {\n  font-family: '${DOC_FONT_FAMILY}';\n  src: url('${baseUrl}${src}') format('truetype');\n  font-weight: ${weight};\n  font-style: ${style};\n  font-display: swap;\n  unicode-range: ${unicodeRange(ranges)};\n}`;
  const faces: string[] = [];
  for (const script of ['latin', 'telugu'] as const) {
    const file = FONTS[script];
    const ranges = script === 'latin' ? latinRanges() : TELUGU_RANGES;
    if (file.variable) faces.push(face(file.regular, '100 900', ranges));
    else {
      faces.push(face(file.regular, '400', ranges));
      if (file.bold) faces.push(face(file.bold, '700', ranges));
    }
  }
  // Italic faces only where a real italic file exists (Latin); Telugu has none and shows upright.
  for (const script of ['latin', 'telugu'] as const) {
    const file = FONTS[script];
    const ranges = script === 'latin' ? latinRanges() : TELUGU_RANGES;
    if (file.italic) faces.push(face(file.italic, '400', ranges, 'italic'));
    if (file.boldItalic) faces.push(face(file.boldItalic, '700', ranges, 'italic'));
  }
  return faces.join('\n');
}

/** pdfmake font table: every style maps to a real file (no faked bold). */
export function pdfFontFiles(resolve: (file: string) => string): Record<string, Record<'normal' | 'bold' | 'italics' | 'bolditalics', string>> {
  const out: Record<string, Record<'normal' | 'bold' | 'italics' | 'bolditalics', string>> = {};
  for (const script of ['latin', 'telugu'] as const) {
    const f = FONTS[script];
    out[PDF_FONT_NAME[script]] = {
      normal: resolve(f.regular),
      bold: resolve(f.bold ?? f.regular),
      // No italic file (Telugu): the upright file, so italic Telugu prints upright as on screen.
      italics: resolve(f.italic ?? f.regular),
      bolditalics: resolve(f.boldItalic ?? f.bold ?? f.regular),
    };
  }
  return out;
}

/* ---------------------------------------------------------- style set */

export const STYLE_TOKENS = [
  'h1', 'h2', 'h3', 'body', 'listItem', 'clauseNumber', 'tableCell', 'tableHeader', 'annexureCaption',
] as const;
export type StyleToken = (typeof STYLE_TOKENS)[number];

export interface StyleSpec {
  /** Points. */
  fontSize: number;
  bold: boolean;
  /** Multiple of font size. Telugu stacks need generous leading. */
  lineHeight: number;
  /** Points: [left, top, right, bottom], pdfmake order. */
  margin: readonly [number, number, number, number];
  alignment: 'left' | 'center' | 'justify';
}

/** Body text is 14 pt (requested 2026-09-22); headings step up from it. */
export const BODY_FONT_SIZE = 14;

export const STYLES: Readonly<Record<StyleToken, StyleSpec>> = {
  h1: { fontSize: 20, bold: true, lineHeight: 1.3, margin: [0, 0, 0, 14], alignment: 'center' },
  h2: { fontSize: 17, bold: true, lineHeight: 1.3, margin: [0, 14, 0, 7], alignment: 'left' },
  h3: { fontSize: 15, bold: true, lineHeight: 1.3, margin: [0, 12, 0, 5], alignment: 'left' },
  body: { fontSize: BODY_FONT_SIZE, bold: false, lineHeight: 1.55, margin: [0, 0, 0, 9], alignment: 'justify' },
  listItem: { fontSize: BODY_FONT_SIZE, bold: false, lineHeight: 1.55, margin: [0, 0, 0, 4], alignment: 'left' },
  clauseNumber: { fontSize: BODY_FONT_SIZE, bold: true, lineHeight: 1.55, margin: [0, 0, 7, 0], alignment: 'left' },
  tableCell: { fontSize: 13, bold: false, lineHeight: 1.4, margin: [0, 2, 0, 2], alignment: 'left' },
  tableHeader: { fontSize: 13, bold: true, lineHeight: 1.4, margin: [0, 2, 0, 2], alignment: 'left' },
  annexureCaption: { fontSize: 12, bold: true, lineHeight: 1.3, margin: [0, 8, 0, 4], alignment: 'left' },
};

/** A4 in points, with the margins ID annexure images must fit inside. */
export const PAGE = {
  size: 'A4',
  widthPt: 595.28,
  heightPt: 841.89,
  margins: [56, 56, 56, 56] as const,
} as const;

/** pdfmake font names; each text run carries one of these (see splitByScript). */
export const PDF_FONT_NAME: Readonly<Record<FontScript, string>> = { telugu: 'DeedTelugu', latin: 'DeedLatin' };

/** pdfmake `styles` dictionary. Fonts are set per run, not per style. */
export function toPdfmakeStyles(): Record<StyleToken, Record<string, unknown>> {
  const out = {} as Record<StyleToken, Record<string, unknown>>;
  for (const token of STYLE_TOKENS) {
    const s = STYLES[token];
    out[token] = { fontSize: s.fontSize, bold: s.bold, lineHeight: s.lineHeight, margin: [...s.margin], alignment: s.alignment };
  }
  return out;
}

/** CSS custom properties for the editor, so on-screen text uses the same numbers. */
export function toCssVariables(): string {
  const lines = [`--doc-font-family: '${DOC_FONT_FAMILY}', serif;`];
  for (const token of STYLE_TOKENS) {
    const s = STYLES[token];
    lines.push(
      `--doc-${token}-size: ${s.fontSize}pt;`,
      `--doc-${token}-weight: ${s.bold ? 700 : 400};`,
      `--doc-${token}-line-height: ${s.lineHeight};`,
      `--doc-${token}-margin: ${s.margin[1]}pt ${s.margin[2]}pt ${s.margin[3]}pt ${s.margin[0]}pt;`,
      `--doc-${token}-align: ${s.alignment};`,
    );
  }
  return `:root {\n  ${lines.join('\n  ')}\n}`;
}

/* ------------------------------------------------- line and paragraph spacing */

/**
 * Owner 10-21 (item 1): per paragraph/heading line spacing and space before/after, from closed lists
 * (like Word's 1.0 / 1.15 / 1.5 / 2.0). "Standard" = not set = the style's own numbers. These presets
 * extend the closed style set; they are the only spacing values anywhere (editor, PDF, Word).
 */
export const LINE_SPACINGS = [1, 1.15, 1.5, 2] as const;
/** Points before/after a paragraph. */
export const SPACE_STEPS_PT = [0, 6, 12, 18, 24] as const;
export type LineSpacing = (typeof LINE_SPACINGS)[number];
export type SpaceStep = (typeof SPACE_STEPS_PT)[number];
/** The shape of a block's spacing (the zod schema BlockSpacing in ast.ts checks it). */
export interface SpacingSpec { line?: LineSpacing | undefined; before?: SpaceStep | undefined; after?: SpaceStep | undefined }

/** The numbers a block prints with: the style's own unless the block sets them. One function for PDF, Word and CSS. */
export function spacedStyle(token: StyleToken, s: SpacingSpec | undefined): { lineHeight: number; margin: [number, number, number, number] } {
  const st = STYLES[token];
  return {
    lineHeight: s?.line ?? st.lineHeight,
    margin: [st.margin[0], s?.before ?? st.margin[1], st.margin[2], s?.after ?? st.margin[3]],
  };
}

/** Editor/preview CSS for the data attributes the editor and the text view write (`.doc [data-line]` beats `.doc p`). */
export function toSpacingCss(): string {
  const rules: string[] = [];
  for (const l of LINE_SPACINGS) rules.push(`.doc [data-line="${l}"] { line-height: ${l}; }`);
  for (const pt of SPACE_STEPS_PT) rules.push(`.doc [data-before="${pt}"] { margin-top: ${pt}pt; }`);
  for (const pt of SPACE_STEPS_PT) rules.push(`.doc [data-after="${pt}"] { margin-bottom: ${pt}pt; }`);
  return rules.join('\n');
}
